import React from 'react'
import {
  PrefillEnvironment,
  prefill,
  PaasResult,
  PaasResponse,
} from './prefill'
import useCallbackFunction from './useCallbackFunction'

type SuccessCallback = (result: PaasResult) => void
type ErrorCallback = (response: PaasResponse) => void
type ExceptionCallback = (error: Error) => void

export function usePrefill<T>(
  environment: PrefillEnvironment,
  onSuccess: SuccessCallback,
  onError: ErrorCallback,
  onException?: ExceptionCallback,
) {
  const [isPrefilling, setPrefilling] = React.useState(false)
  const successCallback = useCallbackFunction(onSuccess)
  const errorCallback = useCallbackFunction(onError)
  const exceptionCallback = useCallbackFunction(onException)

  const startPrefill = React.useCallback(
    async (payload: T) => {
      if (isPrefilling) {
        return
      }

      setPrefilling(true)

      try {
        const response = await prefill(environment, payload)
        if (response.status !== 201) {
          errorCallback.current(response)

          return
        }

        successCallback.current(response.result)
      } catch (error) {
        if (!exceptionCallback.current) {
          throw error
        }

        exceptionCallback.current(error)
      } finally {
        setPrefilling(false)
      }
    },
    [
      environment,
      errorCallback,
      exceptionCallback,
      isPrefilling,
      successCallback,
    ],
  )

  return { isPrefilling, startPrefill }
}
